import CardTitleWithIcon from "@/components/company/card-title-with-icon";
import FieldLabel from "@/components/company/shared/field-label";
import ToggleableBadgeList from "@/components/company/shared/toggleable-badge-list";
import TonePill, { type PillTone } from "@/components/company/shared/tone-pill";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { useTranslation } from "@/hooks/useTranslation";
import { hasMarketData, ownershipKind } from "@/lib/company-data-availability";
import { CompanyDTO } from "@shaarp/common";
import { CompassIcon, LayersIcon, ShieldIcon } from "lucide-react";

const CATEGORIES_COLLAPSED_LIMIT = 10;

type OwnershipDisplay = { label: string; tone: PillTone };

function getOwnershipDisplay(
  rawStatus: string,
  t: (key: string) => string,
): OwnershipDisplay | null {
  switch (ownershipKind(rawStatus)) {
    case "public":
      return {
        label: t("company.insights.market.ownershipPublic"),
        tone: "emerald",
      };
    case "subsidiary":
      return {
        label: t("company.insights.market.ownershipSubsidiary"),
        tone: "sky",
      };
    default:
      return null;
  }
}

type CardCompanyMarketProps = {
  readonly company: CompanyDTO;
};

export default function CardCompanyMarket({ company }: CardCompanyMarketProps) {
  const { t } = useTranslation();

  const isB2B = company.isB2B;
  const categories = company.categories ?? [];
  const rawOwnership = company.business?.ownershipStatus;
  const ownership = rawOwnership ? getOwnershipDisplay(rawOwnership, t) : null;

  const hasB2B = isB2B != null;
  const hasCategories = categories.length > 0;
  const hasOwnership = ownership !== null;

  if (!hasMarketData(company)) return null;

  return (
    <Card className="flex flex-col min-w-0 overflow-hidden">
      <CardHeader>
        <CardTitleWithIcon
          icon={CompassIcon}
          title={t("company.insights.market.title")}
        />
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {(hasB2B || hasOwnership) && (
          <div className="flex flex-wrap items-center gap-2">
            {hasB2B && (
              <TonePill tone={isB2B ? "indigo" : "fuchsia"} icon={LayersIcon}>
                {isB2B
                  ? t("company.insights.market.b2b")
                  : t("company.insights.market.b2c")}
              </TonePill>
            )}
            {ownership && (
              <TonePill tone={ownership.tone} icon={ShieldIcon}>
                {ownership.label}
              </TonePill>
            )}
          </div>
        )}

        {hasCategories && (
          <div className="flex flex-col gap-2">
            <FieldLabel>{t("company.insights.market.categoriesTitle")}</FieldLabel>
            <ToggleableBadgeList
              items={categories}
              initialDisplay={CATEGORIES_COLLAPSED_LIMIT}
              getKey={(c) => c}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
